/**
 * VisuelReglesCirculationAM.jsx — Carte HTML/CSS "Règles de circulation" spécifique Permis AM.
 *
 * Remplace le visuel PNG erroné (qui indiquait "rouler au milieu" alors qu'en
 * France, par défaut, on roule À DROITE).
 *
 * Rendu inspiré du style de la série AM_THEMES (fond blanc, bandeau bleu, badge N°2,
 * pastilles bonnes pratiques / à éviter / à retenir), avec 2 variantes :
 *   - variant='thumb' : compact pour la grille (aspect 4/3)
 *   - variant='focus' : mise en page complète pour la modale
 */
import React from 'react';
import { ShieldCheck, AlertTriangle, Lightbulb, MoveRight } from 'lucide-react';

export function VisuelReglesCirculationAM({ variant = 'thumb' }) {
  const isFocus = variant === 'focus';

  // ─── Version vignette (grille) ───
  if (!isFocus) {
    return (
      <div className="w-full h-full bg-white text-slate-900 overflow-hidden flex flex-col" data-testid="visuel-regles-circulation-am">
        <div className="relative bg-gradient-to-r from-sky-600 via-sky-500 to-cyan-500 px-3 py-2 flex items-center gap-2 shrink-0">
          <div className="shrink-0 w-8 h-8 rounded-full bg-white text-sky-600 font-black flex items-center justify-center text-base shadow">2</div>
          <div className="min-w-0 flex-1 text-white">
            <div className="font-black uppercase tracking-wide text-[13px] leading-none">Règles de circulation</div>
            <div className="opacity-95 text-[10px] leading-tight mt-0.5">En France, je roule à droite</div>
          </div>
          <div className="shrink-0 bg-white rounded-md px-1.5 py-0.5 shadow">
            <span className="text-sky-600 font-black text-[9px]">CODE</span>
            <span className="text-slate-900 font-black text-[9px]">2</span>
            <span className="text-sky-600 font-black text-[9px]">ROUES</span>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center items-center px-3 py-2 gap-2 bg-gradient-to-b from-white via-white to-sky-50/40">
          <div className="flex items-center gap-2.5">
            <div className="w-14 h-14 rounded-full bg-white border-4 border-sky-600 flex items-center justify-center font-black text-sky-600 shadow">
              <MoveRight className="w-6 h-6" />
            </div>
            <div className="text-left leading-tight">
              <div className="font-black text-slate-900 text-lg leading-none">Rouler à <span className="text-sky-600">DROITE</span></div>
              <div className="font-black uppercase tracking-widest text-sky-600 text-[8.5px] mt-0.5">Règle par défaut</div>
              <div className="text-slate-600 text-[9px]">Sur route et en agglomération</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 text-[8.5px] font-black uppercase tracking-wider">Sécurité</span>
            <span className="px-1.5 py-0.5 rounded-full bg-rose-50 border border-rose-300 text-rose-700 text-[8.5px] font-black uppercase tracking-wider">À éviter</span>
            <span className="px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-700 text-[8.5px] font-black uppercase tracking-wider">À retenir</span>
          </div>
        </div>
      </div>
    );
  }

  // ─── Version focus (modale) ───
  return (
    <div className="w-full bg-white text-slate-900 overflow-hidden" data-testid="visuel-regles-circulation-am">
      {/* Bandeau haut */}
      <div className="relative bg-gradient-to-r from-sky-600 via-sky-500 to-cyan-500 px-6 py-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-14 h-14 rounded-full bg-white text-sky-600 font-black flex items-center justify-center text-2xl shadow-md">2</div>
          <div className="min-w-0 flex-1 text-white">
            <div className="font-black uppercase tracking-wider leading-tight text-2xl sm:text-3xl">Les règles de circulation</div>
            <div className="opacity-95 text-base sm:text-lg mt-1 leading-snug">Bien se placer sur la route — spécial Permis AM</div>
          </div>
          <div className="shrink-0 flex flex-col items-end">
            <div className="bg-white rounded-md px-2 py-1 shadow">
              <span className="text-sky-600 font-black text-[11px]">CODE</span>
              <span className="text-slate-900 font-black text-[11px]">2</span>
              <span className="text-sky-600 font-black text-[11px]">ROUES</span>
            </div>
            <div className="mt-1 text-[9px] font-black tracking-widest text-white/90">PERMIS AM · N°2</div>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Bloc central : Rouler à droite */}
        <div className="rounded-2xl border-4 border-sky-500 bg-sky-50 text-center p-5">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-24 h-24 rounded-full bg-white border-4 border-sky-600 flex items-center justify-center text-sky-600 shadow">
              <MoveRight className="w-12 h-12" />
            </div>
            <div className="text-left">
              <div className="font-black leading-none text-slate-900 text-4xl sm:text-5xl">Je roule à <span className="text-sky-600">DROITE</span></div>
              <div className="font-bold uppercase tracking-widest text-sky-600 text-sm mt-2">La règle par défaut en France</div>
            </div>
          </div>
          <p className="text-slate-700 text-base leading-relaxed mt-2">
            En France, la circulation se fait toujours <strong>sur le côté droit de la chaussée</strong>.
            Je me tiens le plus à droite possible, sauf pour dépasser un véhicule plus lent
            ou éviter un obstacle. Puis je reviens à droite dès que possible.
          </p>
        </div>

        {/* 3 pastilles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg bg-emerald-50 border-2 border-emerald-300 p-3">
            <div className="flex items-center gap-1 font-black text-emerald-700 uppercase tracking-wider text-xs mb-2">
              <ShieldCheck className="w-4 h-4" />
              <span>Pour ta sécurité</span>
            </div>
            <ul className="text-slate-800 leading-tight text-sm space-y-1.5 list-disc list-inside">
              <li>Je circule à droite</li>
              <li>Je respecte le marquage au sol</li>
              <li>Je reste dans ma voie</li>
              <li>Je signale mes changements</li>
            </ul>
          </div>
          <div className="rounded-lg bg-rose-50 border-2 border-rose-300 p-3">
            <div className="flex items-center gap-1 font-black text-rose-700 uppercase tracking-wider text-xs mb-2">
              <AlertTriangle className="w-4 h-4" />
              <span>À éviter</span>
            </div>
            <ul className="text-slate-800 leading-tight text-sm space-y-1.5 list-disc list-inside">
              <li>Rouler au milieu de la chaussée</li>
              <li>Chevaucher la ligne blanche</li>
              <li>Franchir une ligne continue</li>
              <li>Rester bloqué à gauche</li>
            </ul>
          </div>
          <div className="rounded-lg bg-amber-50 border-2 border-amber-300 p-3">
            <div className="flex items-center gap-1 font-black text-amber-700 uppercase tracking-wider text-xs mb-2">
              <Lightbulb className="w-4 h-4" />
              <span>À retenir</span>
            </div>
            <div className="text-slate-900 font-bold leading-tight text-base">
              En France, je roule <span className="text-sky-700">à droite</span>. Je me positionne pour être <span className="text-emerald-700">bien vu</span>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VisuelReglesCirculationAM;
